import React, { useState, useEffect, useRef } from 'react';
import '../../styles/tvGoalBanner.css';

const APPROACHING_THRESHOLD = 85;
const CELEBRATION_DURATION  = 9000;
const DONE_BANNER_DURATION  = 60000; // ocultar banner verde 1 min después de que aparece

// Deterministic star/ray data — computed once at module level, never re-created on render
const STARS = Object.freeze(
    Array.from({ length: 22 }, (_, i) => ({
        left:     `${(i * 37 + 7) % 97}%`,
        size:     `${0.55 + (i % 4) * 0.25}vw`,
        delay:    `${((i * 350) % 2500) / 1000}s`,
        duration: `${1.4 + (i % 5) * 0.35}s`,
        color:    ['#ffd700', '#00e5a0', '#ff8c00'][i % 3],
    }))
);

const RAYS = Object.freeze(
    Array.from({ length: 12 }, (_, i) => ({
        angle: `${i * 30}deg`,
        delay: `${(i * 70) / 1000}s`,
    }))
);

function safeLocale(n) {
    const v = Number(n);
    return isNaN(v) ? '—' : v.toLocaleString('es-MX');
}

/**
 * TVGoalBanner
 *
 * Renders nothing when progreso < APPROACHING_THRESHOLD.
 * At 85–99%: animated bottom banner ("¡FALTAN N PIEZAS!").
 * At 100%:   full-screen celebration overlay → compact done banner after 9s.
 *
 * Props:
 *   producido  {number}  Piezas producidas en el turno
 *   meta       {number}  Meta del turno (0 = sin corrida activa → no renderiza)
 *   progreso   {number}  0-100 (ya clampeado)
 */
export default function TVGoalBanner({ producido, meta, progreso }) {
    const [showCelebration, setShowCelebration] = useState(false);
    const [hideDone, setHideDone]               = useState(false);
    const completedRef    = useRef(false);
    const timerRef        = useRef(null);
    const hideDoneTimerRef = useRef(null);

    const numMeta       = Number(meta)      || 0;
    const numProducido  = Number(producido) || 0;
    const piezasRestantes = Math.max(0, numMeta - numProducido);

    const isApproaching = progreso >= APPROACHING_THRESHOLD && progreso < 100;
    const isCompleted   = progreso >= 100;

    // Dispara la celebración sólo la primera vez que se alcanza el 100%
    useEffect(() => {
        if (isCompleted && !completedRef.current) {
            completedRef.current = true;
            setShowCelebration(true);
            setHideDone(false);
            // Ambos timers arrancan simultáneamente — evita anidamiento frágil
            timerRef.current = setTimeout(
                () => setShowCelebration(false),
                CELEBRATION_DURATION
            );
            hideDoneTimerRef.current = setTimeout(
                () => setHideDone(true),
                CELEBRATION_DURATION + DONE_BANNER_DURATION
            );
        }
        if (!isCompleted) {
            completedRef.current = false;
            setShowCelebration(false);
            setHideDone(false);
            clearTimeout(timerRef.current);
            clearTimeout(hideDoneTimerRef.current);
            timerRef.current = null;
            hideDoneTimerRef.current = null;
        }
        return () => {
            completedRef.current = false;
            clearTimeout(timerRef.current);
            clearTimeout(hideDoneTimerRef.current);
            timerRef.current = null;
            hideDoneTimerRef.current = null;
        };
    }, [isCompleted]);

    if (numMeta === 0) return null;
    if (!isApproaching && !isCompleted) return null;
    if (isCompleted && hideDone && !showCelebration) return null;

    // ── Pantalla completa de celebración (primeros 9s al alcanzar 100%) ──────
    if (showCelebration) {
        return (
            <div className="tvg-overlay" role="status" aria-live="polite">
                {/* rayos giratorios de fondo */}
                <div className="tvg-rays-ring">
                    {RAYS.map((r, i) => (
                        <div
                            key={i}
                            className="tvg-ray"
                            style={{ '--ray-angle': r.angle, '--ray-delay': r.delay }}
                        />
                    ))}
                </div>

                {/* anillos de pulso */}
                <div className="tvg-pulse-ring tvg-pulse-ring--1" />
                <div className="tvg-pulse-ring tvg-pulse-ring--2" />
                <div className="tvg-pulse-ring tvg-pulse-ring--3" />

                {/* estrellas cayendo */}
                {STARS.map((s, i) => (
                    <div
                        key={i}
                        className="tvg-star"
                        style={{
                            '--star-left':     s.left,
                            '--star-size':     s.size,
                            '--star-delay':    s.delay,
                            '--star-duration': s.duration,
                            '--star-color':    s.color,
                        }}
                    />
                ))}

                {/* contenido central */}
                <div className="tvg-center">
                    <span className="tvg-trophy" aria-hidden="true">🏆</span>
                    <h2 className="tvg-title">¡META ALCANZADA!</h2>
                    <div className="tvg-celebration-nums">
                        <span className="tvg-celebration-produced">{safeLocale(numProducido)}</span>
                        <span className="tvg-celebration-slash">/</span>
                        <span className="tvg-celebration-target">{safeLocale(numMeta)}</span>
                        <span className="tvg-celebration-unit">piezas</span>
                    </div>
                </div>
            </div>
        );
    }

    // ── Banner inferior: approaching o done ───────────────────────────────────
    const isDone     = isCompleted;
    const bannerMod  = isDone ? ' tvg-banner--done'           : ' tvg-banner--approaching';
    const arcOffset  = ((1 - progreso / 100) * 314.16).toFixed(2);
    const arcFillMod = isDone ? ' tvg-arc-fill--done'         : '';
    const arcWrapMod = isDone ? ' tvg-arc-wrap--done'         : '';
    const countMod   = isDone ? ' tvg-banner-count--done'     : '';
    const labelMod   = isDone ? ' tvg-banner-label--done'     : '';
    const barFillMod = isDone ? ' tvg-banner-bar-fill--done'  : '';

    return (
        <div className={`tvg-banner${bannerMod}`} role="status" aria-live="polite">
            {/* shimmer sweep */}
            <div className="tvg-shimmer" />

            <div className="tvg-banner-inner">

                {/* arco SVG de progreso */}
                <div className={`tvg-arc-wrap${arcWrapMod}`}>
                    <svg className="tvg-arc-svg" viewBox="0 0 120 120" aria-hidden="true">
                        <circle className="tvg-arc-track" cx="60" cy="60" r="50" />
                        <circle
                            className={`tvg-arc-fill${arcFillMod}`}
                            cx="60"
                            cy="60"
                            r="50"
                            style={{ '--arc-offset': arcOffset }}
                        />
                    </svg>
                    <span className="tvg-arc-pct">
                        {isDone ? '✓' : `${progreso}%`}
                    </span>
                </div>

                {/* texto principal */}
                <div className="tvg-banner-text">
                    {isDone ? (
                        <>
                            <span className={`tvg-banner-label${labelMod}`}>¡Meta alcanzada!</span>
                            <div className={`tvg-banner-count${countMod}`}>
                                {safeLocale(numProducido)}
                                <span className="tvg-banner-sep"> / </span>
                                {safeLocale(numMeta)}
                            </div>
                            <span className="tvg-banner-unit">piezas producidas</span>
                        </>
                    ) : (
                        <>
                            <span className="tvg-banner-label">¡Casi llegamos!</span>
                            <div className="tvg-banner-count">
                                {safeLocale(piezasRestantes)}
                            </div>
                            <span className="tvg-banner-unit">piezas para la meta</span>
                        </>
                    )}
                </div>

                {/* barra de refuerzo con contadores */}
                <div className="tvg-banner-bar-wrap">
                    <div className="tvg-banner-bar-track">
                        <div
                            className={`tvg-banner-bar-fill${barFillMod}`}
                            style={{ '--fill-pct': `${progreso}%` }}
                        />
                    </div>
                    <span className="tvg-banner-bar-label">
                        {safeLocale(numProducido)}&nbsp;/&nbsp;{safeLocale(numMeta)}
                    </span>
                </div>

            </div>
        </div>
    );
}
